import React, {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useRef,
} from 'react'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'

const MAX_OPEN_CHATS = 3

/**
 * Optional initialization options applied once when a chat popover is first
 * opened. Used by features like OpenChatButton to seed an AI-suggested draft,
 * pre-fill the input, or pre-select a patient filter. Not persisted across
 * page reloads (intentionally — they describe a one-time intent).
 */
export type OpenChatOpts = {
    /** Auto-fires `suggestChatResponse` with this prompt and seeds the input. */
    aiPrompt?: string
    /** Pre-fills the input draft (only if the user has no existing draft). */
    defaultText?: string
    /** Pre-selects a patient in the chat's patient filter. */
    patientId?: string
}

type OpenChatsContextType = {
    openChatIds: string[]
    openChat: (chatId: string, opts?: OpenChatOpts) => void
    closeChat: (chatId: string) => void
    isOpen: (chatId: string) => boolean
    minimizedChatIds: string[]
    toggleMinimize: (chatId: string) => void
    isMinimized: (chatId: string) => boolean
    /**
     * Returns and clears the opts associated with a chat. Designed to be
     * called once by the chat popover on initial mount so opts are applied
     * exactly once per "open" action.
     */
    consumeChatOpts: (chatId: string) => OpenChatOpts | undefined
}

const OpenChatsContext = createContext<OpenChatsContextType>({
    openChatIds: [],
    openChat: () => {},
    closeChat: () => {},
    isOpen: () => false,
    minimizedChatIds: [],
    toggleMinimize: () => {},
    isMinimized: () => false,
    consumeChatOpts: () => undefined,
})

/**
 * Tracks which chat popovers are currently open and which are minimized.
 * Persisted to localStorage so popovers survive page navigation and reloads.
 * Lives at the root of the app so popovers can render across every page.
 */
export default function OpenChatsProvider({ children }: PropsWithChildren) {
    const [openChatIds, setOpenChatIds] = useLocalStorage<string[]>(
        LocalStorageKeys.openChatPopoverIds,
        [],
    )
    const [minimizedChatIds, setMinimizedChatIds] = useLocalStorage<string[]>(
        LocalStorageKeys.minimizedChatPopoverIds,
        [],
    )
    /**
     * Per-chat opts kept in memory only. We use a ref (not state) so that
     * `consumeChatOpts` can be safely called during render -- no setState
     * means no "setState in render" warning. Opts are intentionally one-time
     * intents tied to a specific user action; they aren't UI state and
     * shouldn't trigger re-renders when added or cleared.
     */
    const chatOptsRef = useRef<Record<string, OpenChatOpts>>({})

    /**
     * Adds the chat to the open list. If already open, brings it to the front
     * of the stack and ensures it's not minimized. Caps total open chats at
     * `MAX_OPEN_CHATS` (oldest dropped).
     */
    const openChat = useCallback(
        (chatId: string, opts?: OpenChatOpts) => {
            setOpenChatIds(prev => {
                const without = prev.filter(id => id !== chatId)
                const next = [chatId, ...without]
                return next.slice(0, MAX_OPEN_CHATS)
            })
            setMinimizedChatIds(prev => prev.filter(id => id !== chatId))
            if (opts) {
                chatOptsRef.current = {
                    ...chatOptsRef.current,
                    [chatId]: opts,
                }
            }
        },
        [setOpenChatIds, setMinimizedChatIds],
    )

    const closeChat = useCallback(
        (chatId: string) => {
            setOpenChatIds(prev => prev.filter(id => id !== chatId))
            setMinimizedChatIds(prev => prev.filter(id => id !== chatId))
            if (chatId in chatOptsRef.current) {
                const { [chatId]: _, ...rest } = chatOptsRef.current
                chatOptsRef.current = rest
            }
        },
        [setOpenChatIds, setMinimizedChatIds],
    )

    const toggleMinimize = useCallback(
        (chatId: string) => {
            setMinimizedChatIds(prev =>
                prev.includes(chatId)
                    ? prev.filter(id => id !== chatId)
                    : [...prev, chatId],
            )
        },
        [setMinimizedChatIds],
    )

    const isOpen = useCallback(
        (chatId: string) => openChatIds.includes(chatId),
        [openChatIds],
    )

    const isMinimized = useCallback(
        (chatId: string) => minimizedChatIds.includes(chatId),
        [minimizedChatIds],
    )

    const consumeChatOpts = useCallback(
        (chatId: string): OpenChatOpts | undefined => {
            const opts = chatOptsRef.current[chatId]
            if (opts) {
                const { [chatId]: _, ...rest } = chatOptsRef.current
                chatOptsRef.current = rest
            }
            return opts
        },
        [],
    )

    return (
        <OpenChatsContext.Provider
            value={{
                openChatIds,
                openChat,
                closeChat,
                isOpen,
                minimizedChatIds,
                toggleMinimize,
                isMinimized,
                consumeChatOpts,
            }}
        >
            {children}
        </OpenChatsContext.Provider>
    )
}

export function useOpenChats() {
    return useContext(OpenChatsContext)
}
